import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { JavapiService } from '../../services/javapi.service';
import { MetodosgenService } from '../../services/metodosgen.service';
import { firstValueFrom } from 'rxjs';
import { DatePipe } from '@angular/common';

interface TimelineEvent {
    date: Date;
    title: string;
    description: string;
    icon: string;
    color: string;
    type: 'status' | 'quote' | 'ars' | 'planilla' | 'creation';
}

@Component({
    selector: 'app-seguimiento',
    templateUrl: './seguimiento.component.html',
    styleUrls: ['./seguimiento.component.scss'],
    standalone: false,
    providers: [DatePipe]
})
export class SeguimientoComponent implements OnInit {

    @Input() trabajo: any;
    @Input() cotizacion: any;
    @Input() billingInfo: any;
    events: TimelineEvent[] = [];
    isLoading = true;

    constructor(
        private modalCtrl: ModalController,
        private javapiService: JavapiService,
        private metodosGenService: MetodosgenService,
        private datePipe: DatePipe
    ) { }

    async ngOnInit() {
        await this.loadAllEvents();
    }

    async loadAllEvents() {
        this.isLoading = true;
        this.events = [];

        try {
            // 1. Resolve Global Context (Job + All related Quotes)
            
            // If we only have a quote, try to get its linked job first
            let tId = this.cotizacion?.trabajoId || this.cotizacion?.trabajo_id;
            
            // Try to extract from dt15 if raw data
            if (!tId && this.cotizacion?.dt15) {
                tId = this.cotizacion.dt15.split('|')[0];
            }

            if (this.cotizacion && !this.trabajo && tId && tId !== '0' && tId !== 0) {
                const jobRes: any = await firstValueFrom(
                    this.javapiService.getCommonModel(
                        this.metodosGenService.setModeloGeneral("genModel", "getTrabajoDetalle", String(tId), '', '', '', '', '', '', '', '', '', '', '', '', '', '')
                    )
                );
                if (jobRes && jobRes.length > 0) {
                    this.trabajo = {
                        id: jobRes[0].dt1,
                        cliente: jobRes[0].dt2,
                        lugar: jobRes[0].dt3,
                        descripcion: jobRes[0].dt4,
                        solicitante: jobRes[0].dt5,
                        incluye_material: jobRes[0].dt6,
                        estado: jobRes[0].dt7,
                        fecha_creacion: jobRes[0].dt8
                    };
                }
            }

            // 2. Load all related quotes if we have a job
            let relatedQuotes: any[] = [];
            if (this.trabajo) {
                // Fetch all quotes for this job
                const quotesRes: any = await firstValueFrom(
                    this.javapiService.getCommonModel(
                        this.metodosGenService.setModeloGeneral('cotizaciones', 'cotizacionesT', String(this.trabajo.id), '', '', '', '', '', '', '', '', '', '', '', '', '', '')
                    )
                );
                if (Array.isArray(quotesRes)) {
                    relatedQuotes = quotesRes.map(q => ({
                        id: q.dt1,
                        fecha: q.dt2,
                        cliente: q.dt3,
                        estado: q.dt5,
                        precio_total: q.dt9 || q.dt4,
                        fecha_creacion: q.dt13,
                        oc_numbers: q.dt15?.split('|')[3] || ''
                    }));
                }
            } else if (this.cotizacion) {
                // Just the current quote
                relatedQuotes = [this.cotizacion];
            }

            // 3. Load Events for the Job
            if (this.trabajo) {
                // Job Creation
                this.addEvent(this.trabajo.fecha_creacion, 'Trabajo: Creado', `Registrado en ${this.trabajo.lugar}.`, 'briefcase', 'primary', 'creation');
                
                // Job Status History & Planilla & ARS
                await Promise.all([
                    this.loadStatusChanges('TRABAJOS', this.trabajo.id),
                    this.loadPlanillaStart(this.trabajo.id),
                    this.loadArsEvents(this.trabajo.id)
                ]);
            }

            // 4. Load Events for ALL related Quotations
            if (relatedQuotes.length > 0) {
                await Promise.all(relatedQuotes.map(quote => this.loadSingleQuoteHistory(quote)));
            }

            // 5. Final Sort
            this.events.sort((a, b) => b.date.getTime() - a.date.getTime());

        } catch (e) {
            console.error('Error in unified tracking', e);
        } finally {
            this.isLoading = false;
        }
    }

    private async loadSingleQuoteHistory(quote: any) {
        if (!quote || !quote.id) return;

        // A. Creation
        const fCreacion = quote.fecha_creacion || quote.c_fecha_creacion || quote.fechaCreacion;
        if (fCreacion) {
            this.addEvent(fCreacion, `Cotización #${quote.id}: Registrada`, `La solicitud se registró en el sistema.`, 'add-circle', 'primary', 'creation');
        }

        // B. Status History
        await this.loadStatusChanges('COTIZACIONES', quote.id, `Cotización #${quote.id}`);

        // C. OC Events
        const ocNumbers = quote.oc_numbers || quote.ocList?.join(', ') || '';
        if (ocNumbers) {
            this.addEvent(quote.fecha || new Date(), `Cotización #${quote.id}: OC Asignada`, `Vinculada a OC: ${ocNumbers}`, 'cart', 'tertiary', 'quote');
        }

        // D. Billing & Payments (If available, otherwise we try to fetch it)
        let bInfo = (quote.id === this.cotizacion?.id) ? this.billingInfo : null;
        if (!bInfo) {
            try {
                const res = await firstValueFrom(this.javapiService.getCommonModel(this.metodosGenService.setModeloGeneral('genModel', 'factura_empresa_get_fiscal', String(quote.id), '', '', '', '', '', '', '', '', '', '', '', '', '', '')));
                if (res && res.length > 0) {
                    bInfo = {
                        facturaFiscal: res[0].dt1, fechaFiscal: res[0].dt2, 
                        fechaPago: res[0].dt4, facturaEmpresa: res[0].dt7, fechaEmpresa: res[0].dt8
                    };
                }
            } catch (e) {}
        }

        if (bInfo) {
            const prefix = `Cotización #${quote.id}`;
            if (bInfo.fechaEmpresa) this.addEvent(bInfo.fechaEmpresa, `${prefix}: Factura Empresa`, `Factura #${bInfo.facturaEmpresa}`, 'document-attach', 'primary', 'quote');
            if (bInfo.fechaFiscal) this.addEvent(bInfo.fechaFiscal, `${prefix}: Factura Fiscal`, `Comprobante #${bInfo.facturaFiscal}`, 'receipt', 'primary', 'quote');
            if (bInfo.fechaPago) this.addEvent(bInfo.fechaPago, `${prefix}: Pago Confirmado`, `Cobro registrado exitosamente.`, 'cash', 'success', 'quote');
        }
    }

    private async loadStatusChanges(modulo: string, id: any, titlePrefix: string = 'Trabajo') {
        try {
            const rutina = (modulo === 'COTIZACIONES') ? 'cotizaciones' : 'genModel';
            const res = await firstValueFrom(this.javapiService.getCommonModel(this.metodosGenService.setModeloGeneral(rutina, "getBitacora", modulo, String(id), '', '', '', '', '', '', '', '', '', '', '', '', '')));
            if (res && res.length > 0 && !(res[0] as any).Error) {
                res.forEach(item => {
                    const comentario = item.dt4 || '';
                    const lowerCom = comentario.toLowerCase();
                    if (lowerCom.includes('cambio de estado a')) {
                        const nuevoEstado = comentario.includes(':') ? comentario.split(':')[1]?.trim() : lowerCom.split('cambio de estado a')[1]?.trim();
                        if (nuevoEstado) {
                            this.addEvent(item.dt1, `${titlePrefix}: ${nuevoEstado}`, `Por ${item.dt5 || 'Usuario'}`, this.getStatusIcon(nuevoEstado), this.getStatusColor(nuevoEstado), 'status');
                        }
                    } else {
                        // Evento General (no es cambio de estado)
                        let icon = 'list-circle';
                        let color = 'medium';
                        
                        // Detectar si es un evento de ARS para ponerle un icono mejor
                        if (lowerCom.includes('ars')) {
                            icon = 'document-attach-outline';
                            color = 'tertiary';
                        }

                        this.addEvent(item.dt1, `${titlePrefix}: ${comentario}`, `Por ${item.dt5 || 'Usuario'}`, icon, color, 'status');
                    }
                });
            }
        } catch (e) {}
    }

    private async loadPlanillaStart(trabajoId: any) {
        try {
            const res = await firstValueFrom(this.javapiService.getCommonModel(this.metodosGenService.setModeloGeneral('genModel', 'menuTotals', String(trabajoId), '', '', '', '', '', '', '', '', '', '', '', '', '', '')));
            if (res && res.length > 0 && res[0].dt4) {
                this.addEvent(res[0].dt4, 'Trabajo: Inicio de Labores', 'Primer registro de asistencia en planilla.', 'play', 'success', 'planilla');
            }
        } catch (e) {}
    }

    private async loadArsEvents(trabajoId: any) {
        try {
            const res = await firstValueFrom(this.javapiService.getCommonModel(this.metodosGenService.setModeloGeneral("genModel", "getArsInfo", String(trabajoId), '', '', '', '', '', '', '', '', '', '', '', '', '', '')));
            if (res && res.length > 0 && res[0].dt1 === 'Y' && res[0].dt2) {
                this.addEvent(res[0].dt2, 'Trabajo: ARS Firmado', 'Reporte de servicio firmado y validado.', 'checkmark-done-circle', 'success', 'ars');
            }
        } catch (e) {}
    }

    getStatusIcon(estado: string): string {
        const e = (estado || '').toLowerCase();
        if (e === 'en proceso') return 'play-forward';
        if (e === 'finalizado' || e === 'aprobada') return 'checkmark-circle';
        if (e === 'borrador') return 'document';
        if (e === 'enviada') return 'send';
        if (e === 'en pausa' || e === 'en revision' || e === 'esperando datos') return 'time';
        if (e === 'corregir') return 'alert-circle';
        if (e === 'cancelado' || e === 'rechazada' || e === 'cancelada') return 'close-circle';
        return 'ellipse';
    }

    getStatusColor(estado: string): string {
        const e = (estado || '').toLowerCase();
        if (e === 'en proceso') return 'primary';
        if (e === 'finalizado' || e === 'aprobada') return 'success';
        if (e === 'borrador') return 'medium';
        if (e === 'enviada') return 'secondary';
        if (e === 'en pausa' || e === 'en revision' || e === 'esperando datos') return 'warning';
        if (e === 'corregir') return 'warning';
        if (e === 'cancelado' || e === 'rechazada' || e === 'cancelada') return 'danger';
        return 'medium';
    }

    private addEvent(dateStr: any, title: string, description: string, icon: string, color: string, type: any) {
        if (!dateStr) return;

        let date: Date;
        if (typeof dateStr === 'string') {
            const parts = dateStr.match(/\d+/g);
            if (parts && parts.length >= 3) {
                // Determine format
                const isISO = parts[0].length === 4;
                const year = isISO ? Number(parts[0]) : Number(parts[2]);
                const month = Number(parts[1]) - 1;
                const day = isISO ? Number(parts[2]) : Number(parts[0]);
                const hour = parts.length >= 5 ? Number(parts[3]) : (parts.length === 4 ? Number(parts[3]) : 0);
                const min = parts.length >= 5 ? Number(parts[4]) : 0;
                
                date = new Date(year, month, day, hour, min, 0, 0);

                // Handle AM/PM manually if present
                const lowerStr = dateStr.toLowerCase();
                if (lowerStr.includes('pm') && hour < 12) date.setHours(hour + 12);
                if (lowerStr.includes('am') && hour === 12) date.setHours(0);
            } else {
                date = new Date(dateStr);
            }
        } else if (dateStr instanceof Date) {
            date = dateStr;
        } else {
            date = new Date(dateStr);
        }

        if (isNaN(date.getTime())) return;
        this.events.push({ date, title, description, icon, color, type });
    }



    close() {
        this.modalCtrl.dismiss();
    }

}
